import { useEffect, useRef, useState } from "react";
import "./CeldaEditable.css";

/**
 * Un dato de la tabla que se edita en su lugar.
 *
 * Se hace clic encima, se escribe y se guarda con Enter o al salir del campo;
 * Escape deja las cosas como estaban. Mientras el servidor responde el campo
 * queda deshabilitado, para que no se mande dos veces lo mismo.
 *
 * `onGuardar` recibe el valor nuevo y devuelve una promesa. Si truena, el
 * valor vuelve al anterior y el error se avisa afuera.
 */
export default function CeldaEditable({
  valor,
  onGuardar,
  placeholder = "Sin nombre",
  tipo = "text",
  className = "",
  titulo = "Clic para editar",
}) {
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState(valor ?? "");
  const [guardando, setGuardando] = useState(false);
  const campo = useRef(null);

  useEffect(() => {
    if (!editando) setBorrador(valor ?? "");
  }, [valor, editando]);

  useEffect(() => {
    if (editando && campo.current) {
      campo.current.focus();
      campo.current.select();
    }
  }, [editando]);

  const cancelar = () => {
    setBorrador(valor ?? "");
    setEditando(false);
  };

  const guardar = async () => {
    const limpio = String(borrador).trim();

    if (limpio === String(valor ?? "").trim()) {
      setEditando(false);
      return;
    }

    setGuardando(true);
    try {
      await onGuardar(limpio);
      setEditando(false);
    } catch (error) {
      setBorrador(valor ?? "");
      setEditando(false);
    } finally {
      setGuardando(false);
    }
  };

  if (!editando) {
    return (
      <button
        type="button"
        className={`celda-editable ${className}`}
        onClick={() => setEditando(true)}
        title={titulo}
      >
        {valor === null || valor === undefined || valor === "" ? (
          <span className="celda-editable-vacia">{placeholder}</span>
        ) : (
          valor
        )}
      </button>
    );
  }

  return (
    <input
      ref={campo}
      type={tipo}
      className={`celda-editable-campo ${className}`}
      value={borrador}
      disabled={guardando}
      onChange={(e) => setBorrador(e.target.value)}
      onBlur={guardar}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          guardar();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          cancelar();
        }
      }}
    />
  );
}
